import type { ValueOf } from 'type-fest';

import { MM_PER_INCH } from '@frontend/partviewer/src/constants';

import { UNITS } from 'constants/all';

export type DisplayUnits = ValueOf<typeof UNITS>;

export type SceneUnits = {
  /**
   * The unit the drawing's own coordinates are in, or `null` when resolved units
   * are unknown (`$INSUNITS` 0 / missing). A unitless file gets a neutral "u"
   * suffix rather than being silently presented as inches.
   */
  displayUnits: DisplayUnits | null;
  /** Multiply a drawing coordinate by this to get `displayUnits`. */
  scaleToDisplayUnits: number;
  /** True when the file declares no unit at all. */
  isUnitless: boolean;
};

/**
 * `$INSUNITS` code → how many millimetres one drawing unit is.
 *
 * Only the codes a mechanical drawing plausibly uses are listed; anything else
 * (parsecs, light years, and the rest of the AutoCAD tail) falls through to
 * unitless, which is more honest than guessing.
 */
const MM_PER_UNIT: Readonly<Record<number, number>> = {
  1: MM_PER_INCH, // inches
  2: MM_PER_INCH * 12, // feet
  4: 1, // millimetres
  5: 10, // centimetres
  6: 1000, // metres
  8: MM_PER_INCH / 1_000_000, // microinches
  9: MM_PER_INCH / 1000, // mils
  10: MM_PER_INCH * 36, // yards
  12: 1e-6, // nanometres
  13: 1e-3, // microns
  14: 100, // decimetres
  21: MM_PER_INCH * 12, // US survey feet
  22: MM_PER_INCH, // US survey inch
  23: MM_PER_INCH * 36, // US survey yard
};

/** Codes that are natively imperial, so measurements read best in inches. */
const IMPERIAL_CODES: ReadonlySet<number> = new Set([
  1, 2, 8, 9, 10, 21, 22, 23,
]);

/**
 * Resolves a DXF `$INSUNITS` code (from `PartFile.dxfSceneState.units` /
 * `Drawing.resolved_units`) into what the viewer should display.
 *
 * Values default to the drawing's resolved native unit — this is that
 * resolution. It maps to one of the two units the viewer formats in rather than
 * preserving the file's exact unit, because a drawing authored in survey feet
 * still wants reading in inches.
 */
export const resolveSceneUnits = (insunits: number): SceneUnits => {
  const millimetresPerUnit = MM_PER_UNIT[insunits];

  if (millimetresPerUnit === undefined) {
    return {
      displayUnits: null,
      scaleToDisplayUnits: 1,
      isUnitless: true,
    };
  }

  const isImperial = IMPERIAL_CODES.has(insunits);
  return {
    displayUnits: isImperial ? UNITS.IN : UNITS.MM,
    scaleToDisplayUnits: isImperial
      ? millimetresPerUnit / MM_PER_INCH
      : millimetresPerUnit,
    isUnitless: false,
  };
};
